import { getForegroundColor } from "../utils/getForegroundColor";
import { fontFamilyStack, isSupportedFont } from "../utils/googleFonts";
import { mixColors } from "../utils/mixColors";
import type { ThemeMode } from "./resolveThemeMode";

const RADIUS_SCALE: Record<CornerRadius, string> = {
  sharp: "0",
  regular: "1",
  rounded: "4",
};

/**
 * Values for the CSS custom properties the theme controls, keyed by var name.
 * An `undefined` value means "leave this var unset" so the built-in token
 * default in index.css applies — that's how the dark neutral palette (or the
 * light defaults) show through for any field the merchant didn't customize.
 */
export type ThemeVars = Record<string, string | undefined>;

export function resolveThemeTokens(
  brand: BrandSettings,
  mode: ThemeMode
): ThemeVars {
  const config = (mode === "dark" ? brand.dark : brand.light) ?? {};

  // Brand colors fall back to the base top-level fields (always defined).
  const primary = config.brand_color ?? brand.brand_primary_color;
  const secondary = config.accent_color ?? brand.brand_accent_color;

  return {
    "--color-primary": primary,
    "--color-secondary": secondary,
    // Legible text color (white/black, WCAG-chosen) for use ON the brand
    // surfaces — e.g. text-primary-foreground on a bg-primary element.
    "--color-primary-foreground": getForegroundColor(primary),
    "--color-secondary-foreground": getForegroundColor(secondary),

    "--color-surface": config.background_color,
    // A subtle contrast layer derived from the surface (surface mixed with 2%
    // black in light, 2% white in dark)
    "--color-surface-muted": config.background_color
      ? mixColors(
          config.background_color,
          mode === "dark" ? "#ffffff" : "#000000",
          0.02
        )
      : undefined,
    "--color-header": config.header_color,
    "--color-text": config.primary_text_color,
    "--color-text-muted": config.secondary_text_color,
    "--color-border": config.border_color,
    "--radius-scale": RADIUS_SCALE[brand.corner_radius ?? "regular"],
    "--font-family": isSupportedFont(brand.font_family)
      ? fontFamilyStack(brand.font_family)
      : undefined,
  };
}
